import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import * as ApiCalls from "./Servings/ApiCalls.tsx";

import Loading from "./Servings/Loading.tsx";

import "../style/general.css";

import { convertBookInfo, changePageTitle, refineBookId } from "./Utils.ts";

import NavigationBar from "./Servings/NavigationBar.tsx";
import ErrorPage from "./ErrorPage.tsx";
import DescriptionBox from "./Servings/DescriptionBox.tsx";
import CoverColumn from "./Servings/CoverColumn.tsx";
import TitleAndAuthors from "./Servings/TitleAndAuthors.tsx";
import { ErrorType } from "./Types/ErrorType.ts";

export default function PurchasePage() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ErrorType>();
  const [isVippsLoading, setIsVippsLoading] = useState<boolean>(false);
  const [transactionId, setTransactionId] = useState<any>(null);
  const [bookInfo, setBookInfo] = useState<any>(null);
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false);

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQuery();

  const bookId = refineBookId(query.get("bookid") ?? "");
  const userId = query.get("userid") ?? "";

  useEffect(() => {
    if (bookId && userId) {
      const getBookAndTransactionId = ApiCalls.getBookAndTransactionId(bookId, userId);
      const lovdataUserIdValidation = ApiCalls.lovdataUserIdValidation(userId);

      getBookAndTransactionId.catch((err) => {
        console.log("getBookAndTransactionId failed", err);
        setError(ErrorType.BFFAPICallError);
      });

      lovdataUserIdValidation.catch((err) => {
        console.log("lovdataUserIdValidation failed", err);
        setError(ErrorType.LovdataAPICallError);
      });

      Promise.all([getBookAndTransactionId, lovdataUserIdValidation]).then(([bookResponse, lovdataResponse]) => {
        const newBookInfo = convertBookInfo(bookResponse, bookId);

        if (lovdataResponse && lovdataResponse.IsActive) {
          if (newBookInfo && newBookInfo !== false) {
            setTransactionId(bookResponse.TransactionId);
            setBookInfo(newBookInfo);
            changePageTitle(newBookInfo.title);

            if (bookResponse && bookResponse.TransactionState === "AlreadyPurchased") {
              setError(ErrorType.AlreadyPurchased);
            }
          } else {
            console.log("bookInfo is false");
            setError(ErrorType.NoMetadataForBook);
          }
        } else {
          setError(ErrorType.LovdataUserIdNotActive);
        }

        setIsLoading(false);
      });
    } else {
      if (!bookId) {
        setError(ErrorType.BookIdNotFound);
      } else if (!userId) {
        setError(ErrorType.LovdataUserIdNotFound);
      }
    }
  }, [bookId, userId]);

  const createPayment = async () => {
    if (bookId && userId) {
      setIsVippsLoading(true);
      ApiCalls.createVippsTransaction(bookId, userId, transactionId)
        .then(async (response: any) => {
          if (response?.redirectUrl) {
            window.location.assign(response.redirectUrl);
          } else {
            setError(ErrorType.NotGettingRedirectUrlFromVipps);
          }
        })
        .catch((error) => {
          console.log("Error in createPayment", error);
          setError(ErrorType.VippsAPICallError);
        })
        .finally(() => setIsVippsLoading(false));
    }
  };

  function VippsButton(props: any) {
    const { props: isTermsAccepted } = props;
    // Since disabled="true" breaks the button, we need to use a workaround by creating two buttons
    return (
      <>
        {isTermsAccepted ? (
          <vipps-mobilepay-button
            className="vipps-button"
            type="button"
            brand="vipps"
            language="no"
            variant="primary"
            rounded="true"
            verb="buy"
            stretched="false"
            branded="true"
            loading={isVippsLoading ? "true" : "false"}
            onClick={() => createPayment()}
          ></vipps-mobilepay-button>
        ) : (
          <vipps-mobilepay-button
            className="vipps-button"
            type="button"
            brand="vipps"
            language="no"
            variant="primary"
            rounded="true"
            verb="buy"
            stretched="false"
            branded="true"
            title="Vilkår bør aksepteres først"
            loading={isVippsLoading ? "true" : "false"}
            onClick={() => createPayment()}
            disabled="true"
          ></vipps-mobilepay-button>
        )}
      </>
    );
  }

  return (
    <>
      {error ? (
        <ErrorPage errorType={error} bookInfo={bookInfo ?? ""} />
      ) : (
        <>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <NavigationBar bookInfo={bookInfo} />
              <div className="main-container">
                <CoverColumn bookInfo={bookInfo} />

                <div className="second-column">
                  <TitleAndAuthors bookInfo={bookInfo} />
                  <div className="BookPrice">
                    Pris: {bookInfo.BookPriceInNok ? bookInfo.BookPriceInNok + ",- kr" : "Prisen er ikke satt."}
                  </div>
                  <div className="checkbox-container checkbox-wrapper-46">
                    <input
                      type="checkbox"
                      id="cbx-46"
                      className="checkbox-input inp-cbx"
                      onChange={() => setIsTermsAccepted(!isTermsAccepted)}
                    />
                    <label htmlFor="cbx-46" className="checkbox-label cbx">
                      <span>
                        <svg viewBox="0 0 12 10" height="10px" width="10px">
                          <polyline points="1.5 6 4.5 9 10.5 1"></polyline>
                        </svg>
                      </span>
                      <span style={{ display: "inline", fontSize: "0.9em" }}>
                        Jeg samtykker med dette i at jeg straks skal motta {bookInfo.title}, og erkjenner samtidig at angreretten
                        bortfaller så snart nedlastingen eller tilgangen starter.
                      </span>
                    </label>
                  </div>
                  <div className="VippsPaymentButtonContainer">
                    {bookInfo.BookPriceInNok && <VippsButton props={isTermsAccepted} />}
                  </div>
                  <div className="ExtraInfoContainer">
                    <div>
                      <strong>Digital utgave:</strong> Lovdata Pro Karnov
                    </div>
                    <div>
                      <strong>Umiddelbart tilgang</strong>
                    </div>
                  </div>
                </div>
              </div>
              <DescriptionBox bookInfo={bookInfo} />
            </>
          )}
        </>
      )}
    </>
  );
}
